/* ============================================================================
 * SKINFORGE — forging a skin at runtime
 * ----------------------------------------------------------------------------
 * The Skin builder's model. A skin forged here is DATA, not a file: it applies
 * by writing CSS custom properties onto <html>, so a Virtuals agent (or an
 * operator) can create one with no code change and no deploy.
 *
 * WHAT A FORGED SKIN MAY CHANGE — settled, not open:
 *   • the palette            (the raw tokens every built-in skin defines)
 *   • safe structural knobs  density, borders, radius
 *
 * WHAT IT MAY NOT CHANGE:
 *   • free-form geometry. Bar heights, dock size, the bevel, the CRT field and
 *     the type stack belong to the master. A fork that could reshape the shell
 *     would stop being a fork of one system.
 *
 * HOW IT APPLIES: a forged skin sets `data-skin` to the master (so structure and
 * the semantic layer resolve) and then writes its palette and structural knobs
 * as INLINE custom properties. Inline declarations beat the stylesheet, so the
 * palette wins without touching any CSS file.
 *
 * LAYERING, unchanged:
 *   master structure  →  forged skin palette  →  personal knobs (appearance)
 * The personal knobs still stack on top; that was the settled answer.
 * ========================================================================= */

export interface SkinToken {
  /** Raw token name as the stylesheets declare it. */
  name: string;
  label: string;
  /** Value in the master (OceanSide) — the starting point for a new forge. */
  master: string;
  /** True ⇒ it is a colour and gets a swatch/colour input. */
  colour: boolean;
}

export interface TokenGroup {
  id: 'ground' | 'ink' | 'accent' | 'rules' | 'signals';
  label: string;
  note: string;
  tokens: SkinToken[];
}

/** The raw tokens a skin may override, grouped the way they are reasoned about. */
export const TOKEN_GROUPS: readonly TokenGroup[] = [
  {
    id: 'ground',
    label: 'Ground',
    note: 'The canvas and the surfaces stacked on it.',
    tokens: [
      { name: '--navy', label: 'Canvas', master: '#1e1e20', colour: true },
      { name: '--surface', label: 'Surface', master: '#2a2a2c', colour: true },
      { name: '--elevated', label: 'Elevated', master: '#343438', colour: true },
      { name: '--well', label: 'Well', master: '#141416', colour: true },
      { name: '--nested', label: 'Nested', master: '#2f2f31', colour: true },
    ],
  },
  {
    id: 'ink',
    label: 'Ink',
    note: 'Text and metadata. `info` is the secondary role, not a second accent.',
    tokens: [
      { name: '--cream', label: 'Text', master: '#f2f2f7', colour: true },
      { name: '--muted', label: 'Muted', master: '#9a9aa0', colour: true },
      { name: '--info', label: 'Info', master: '#8e8e93', colour: true },
      { name: '--dim', label: 'Dim', master: '#5f5f66', colour: true },
    ],
  },
  {
    id: 'accent',
    label: 'Accent',
    note: 'Gold in the master. Keep it scarce — it is the single primary affordance.',
    tokens: [
      { name: '--gold', label: 'Accent', master: '#b9b9c1', colour: true },
      { name: '--gold-hover', label: 'Accent hover', master: '#cfcfd6', colour: true },
      { name: '--gold-active', label: 'Accent active', master: '#9a9aa2', colour: true },
      { name: '--gold-bg', label: 'Accent background', master: '#2a2a2e', colour: true },
      { name: '--gold-line', label: 'Accent rule', master: 'rgba(185, 185, 193, 0.45)', colour: true },
    ],
  },
  {
    id: 'rules',
    label: 'Rules',
    note: 'Hairlines. Structure is drawn with these, not with fills.',
    tokens: [
      { name: '--border', label: 'Border', master: '#3a3a3e', colour: true },
      { name: '--hair', label: 'Hairline', master: '#2a2a2c', colour: true },
    ],
  },
  {
    id: 'signals',
    label: 'Signals',
    note: 'State colours. Changing these changes what "live" and "fault" look like.',
    tokens: [
      { name: '--hud', label: 'HUD', master: '#8e8e93', colour: true },
      { name: '--live', label: 'Live', master: '#4ade80', colour: true },
      { name: '--fault', label: 'Fault', master: '#dc2626', colour: true },
    ],
  },
] as const;

export const ALL_TOKENS: readonly SkinToken[] = TOKEN_GROUPS.flatMap(
  (g) => g.tokens,
);

/* ── safe structural knobs ────────────────────────────────────────────────── */

export interface StructuralKnobs {
  density: 'canon' | 'comfortable' | 'compact';
  borders: 'canon' | 'hairline' | 'none';
  radius: 'canon' | '0' | '2' | '4';
}

export const CANON_KNOBS: StructuralKnobs = {
  density: 'canon',
  borders: 'canon',
  radius: 'canon',
};

/** Only the three settled switches. No free-form geometry. */
export const STRUCTURAL_OPTIONS: {
  key: keyof StructuralKnobs;
  label: string;
  note: string;
  options: string[];
}[] = [
  {
    key: 'density',
    label: 'Density',
    note: 'Padding, block rhythm and base type size.',
    options: ['canon', 'comfortable', 'compact'],
  },
  {
    key: 'borders',
    label: 'Borders',
    note: 'Hairline rules, or none at all.',
    options: ['canon', 'hairline', 'none'],
  },
  {
    key: 'radius',
    label: 'Radius',
    note: 'Capped at 4px by the system. No pills.',
    options: ['canon', '0', '2', '4'],
  },
];

/* ── the forged skin ──────────────────────────────────────────────────────── */

export interface ForgedSkin {
  /** Slug. Becomes the storage key and the exported file name. */
  id: string;
  label: string;
  /** Wallpaper wordmark. */
  wordmark: string;
  /** Wallpaper name — bought through us, or the operator's own ENS. */
  handle: string;
  nameProvenance: 'issued' | 'brought';
  /** Raw token overrides. A token absent here keeps the master's value. */
  palette: Record<string, string>;
  structural: StructuralKnobs;
}

export function blankSkin(seedLabel = 'New skin'): ForgedSkin {
  return {
    id: slug(seedLabel),
    label: seedLabel,
    wordmark: seedLabel.toUpperCase(),
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    palette: {},
    structural: { ...CANON_KNOBS },
  };
}

export function slug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'skin'
  );
}

/** Tokens actually differing from the master — the skin's real diff. */
export function diffFromMaster(skin: ForgedSkin): SkinToken[] {
  return ALL_TOKENS.filter(
    (t) => skin.palette[t.name] !== undefined && skin.palette[t.name] !== t.master,
  );
}

/* ── runtime application ──────────────────────────────────────────────────── */

/** Every property this module may write, so a previous skin can be cleared. */
const FORGEABLE = new Set(
  ALL_TOKENS.map((t) => t.name).concat([
    '--ui-radius',
    '--ui-radius-ctl',
    '--ui-bw',
    '--ui-row-h',
    '--ui-taskbar-h',
    '--ui-win-pad',
  ]),
);

/**
 * Write the skin onto <html> as inline custom properties and switch `data-skin`
 * to the master so structure resolves. Pass `null` to clear.
 */
export function applyForgedSkin(skin: ForgedSkin | null): void {
  const root = document.documentElement;
  for (const prop of FORGEABLE) root.style.removeProperty(prop);
  if (!skin) return;

  root.setAttribute('data-skin', 'oceanside');

  for (const [name, value] of Object.entries(skin.palette)) {
    if (value) root.style.setProperty(name, value);
  }

  // Structural knobs map onto the same variables the appearance layer uses.
  if (skin.structural.radius !== 'canon') {
    const r = skin.structural.radius === '0' ? '0' : `${skin.structural.radius}px`;
    root.style.setProperty('--ui-radius', r);
    root.style.setProperty('--ui-radius-ctl', r);
  }
  if (skin.structural.borders === 'none') {
    root.style.setProperty('--ui-bw', '0px');
  } else if (skin.structural.borders === 'hairline') {
    root.style.setProperty('--ui-bw', '1px');
  }
  if (skin.structural.density === 'compact') {
    root.style.setProperty('--ui-row-h', '26px');
    root.style.setProperty('--ui-taskbar-h', '56px');
    root.style.setProperty('--ui-win-pad', '8px');
  }
}

/* ── export ───────────────────────────────────────────────────────────────── */

function cssVarBlock(skin: ForgedSkin, indent: string): string {
  const lines: string[] = [];
  for (const t of ALL_TOKENS) {
    const v = skin.palette[t.name];
    if (v && v !== t.master) lines.push(`${indent}${t.name}: ${v};`);
  }
  if (skin.structural.radius !== 'canon') {
    const r = skin.structural.radius === '0' ? '0' : `${skin.structural.radius}px`;
    lines.push(`${indent}--ui-radius: ${r};`);
    lines.push(`${indent}--ui-radius-ctl: ${r};`);
  }
  return lines.join('\n');
}

/** A committable skin file: the snippet a developer drops into skins/. */
export function skinToCss(skin: ForgedSkin): string {
  const block = cssVarBlock(skin, '  ');
  const body =
    block.length > 0
      ? block
      : '  /* no overrides — identical to the master skin */';
  return `/* Forged by SKIN_BUILDER.EXE
 * ${skin.label} — ${skin.wordmark}
 * name: ${skin.handle} (${skin.nameProvenance})
 *
 * A fork overrides the raw palette only. supercompute.css aliases the semantic
 * layer to these tokens and custom properties substitute lazily, so structure is
 * inherited whole. Keep it that way.
 */
[data-skin='${skin.id}'] {
${body}
}
`;
}

export interface SkinExport {
  version: 1;
  forgedAt: string;
  skin: ForgedSkin;
}

export function skinToJson(skin: ForgedSkin): string {
  const payload: SkinExport = {
    version: 1,
    forgedAt: new Date().toISOString(),
    skin,
  };
  return JSON.stringify(payload, null, 2);
}

export function jsonToSkin(json: string): ForgedSkin {
  const parsed = JSON.parse(json) as unknown;
  const raw =
    parsed && typeof parsed === 'object' && 'skin' in (parsed as object)
      ? (parsed as SkinExport).skin
      : (parsed as ForgedSkin);

  if (!raw || typeof raw !== 'object') throw new Error('Not a skin payload.');

  const r = raw as unknown as Record<string, unknown>;
  const palette: Record<string, string> = {};
  if (r.palette && typeof r.palette === 'object') {
    for (const t of ALL_TOKENS) {
      const v = (r.palette as Record<string, unknown>)[t.name];
      if (typeof v === 'string' && v.trim()) palette[t.name] = v.trim();
    }
  }

  const s = (r.structural ?? {}) as Partial<StructuralKnobs>;
  const pick = <T extends string>(v: unknown, allowed: readonly T[], d: T): T =>
    typeof v === 'string' && (allowed as readonly string[]).includes(v)
      ? (v as T)
      : d;

  return {
    id: slug(typeof r.id === 'string' && r.id ? r.id : 'exported-skin'),
    label: typeof r.label === 'string' && r.label ? r.label : 'Exported skin',
    wordmark:
      typeof r.wordmark === 'string' && r.wordmark
        ? r.wordmark
        : 'SUPERCOMPUTE',
    handle:
      typeof r.handle === 'string' && r.handle ? r.handle : 'supercompute.eth',
    nameProvenance: pick(r.nameProvenance, ['issued', 'brought'] as const, 'issued'),
    palette,
    structural: {
      density: pick(s.density, ['canon', 'comfortable', 'compact'] as const, 'canon'),
      borders: pick(s.borders, ['canon', 'hairline', 'none'] as const, 'canon'),
      radius: pick(s.radius, ['canon', '0', '2', '4'] as const, 'canon'),
    },
  };
}

export const FORGE_STORAGE_KEY = 'supercompute-webos:forged-skins:v1';
